/**
 * Non-react access to query parameters. This is outside of the history
 * object of the app, and would not, for example cause re-render on changes
 */
export const getQueryParams = () => {
  const locationParams = new URLSearchParams(window.location.search);

  return locationParams;
};

export const noop = () => {};

export const noopAsync = async () => {};

export type DeepPartial<T> = T extends object
  ? // Same reasoning as the below
    T extends Map<any, any>
    ? T
    : // Avoid going deep into arbitrary fields - Requires key value map shenanigans to get types working again if you're merging two objects together
    Record<string, any> extends T
    ? T
    : // Avoids creating undefine-able array items - Also generally you don't want deep partial to go all the way to individual items
    T extends Array<any>
    ? T
    : T extends (...args: any[]) => any
    ? T
    : // Accounts for nominally typed strings
    T extends string
    ? T
    : { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

export type Unpromise<T> = T extends Promise<infer K> ? K : T;

/**
 * Returns a versioned style object based on the newLook condition callback.
 * @param styles - The styles object to version. Typically a module.css object.
 * @param isNewLookEnabled - Whether to use the new look version of the style.
 * @param version - The version number to use for the new look version of the style.
 * @returns A versioned style object.
 *
 * @example
 * // File: example.module.css
 * .button {
 *   background-color: red;
 * }
 * .button-v1 {
 *   background-color: blue;
 * }
 * .button-v2 {
 *   background-color: green;
 * }
 * // end example.module.css
 *
 * // File: example.tsx
 * import styles from './example.module.css';
 * import { isFeatureEnabled } from '@canvas/client-feature-flags';
 *
 * export const Example = () => {
 *   const isNewLookEnabled = () => isFeatureEnabled('new-look');
 *   const style = versionedStyle(styles, isNewLookEnabled);
 *   return (
 *     <div>
 *       <div className={style('button')}>Button</div>
 *       <div className={style('button', 1)}>Button v1</div>
 *       <div className={style('button', 2)}>Button v2</div>
 *     </div>
 *   );
 * };
 * // end example.tsx
 */
export const versionedStyle =
  (styles: Record<string, string>, isNewLookEnabled = () => false) =>
  (key: keyof typeof styles, version?: number) => {
    const newKey = `${key}${version ? `-v${version}` : ''}`;
    const style = isNewLookEnabled() ? styles[newKey] : styles[key];
    // If new style is not found, return the original key
    return style ?? styles[key];
  };
